import { SmsCommandIntent, SmsCommands } from '@harvestiq/constants';

export type SmsCommandIntentType =
  (typeof SmsCommandIntent)[keyof typeof SmsCommandIntent];

const SmsOptInKeywordCommands = {
  START: 'START',
  YES: 'YES',
  SUBSCRIBE: 'SUBSCRIBE',
  JOIN: 'JOIN',
  SIGNUP: 'SIGNUP',
  OPTIN: 'OPTIN',
  GO: 'GO',
  UNSTOP: 'UNSTOP',
  RESUME: 'RESUME',
  CONTINUE: 'CONTINUE',
  CONFIRM: 'CONFIRM',
  ACTIVATE: 'ACTIVATE',
  ENABLE: 'ENABLE',
  REGISTER: 'REGISTER',
  ADD: 'ADD',
  OK: 'OK',
  ALLOW: 'ALLOW',
  AGREE: 'AGREE',
} as const;

const SmsOptOutKeywordCommands = {
  STOP: 'STOP',
  UNSUBSCRIBE: 'UNSUBSCRIBE',
  CANCEL: 'CANCEL',
  END: 'END',
  QUIT: 'QUIT',
  STOPALL: 'STOPALL',
  OPTOUT: 'OPTOUT',
  REMOVE: 'REMOVE',
  DELETE: 'DELETE',
  BLOCK: 'BLOCK',
  NO: 'NO',
  DISABLE: 'DISABLE',
  LEAVE: 'LEAVE',
  PAUSE: 'PAUSE',
} as const;

const smsCommandKeywordAliases = {
  [SmsCommandIntent.OPT_IN]: [
    SmsCommands.GET_QUOTES,
    SmsOptInKeywordCommands.START,
    SmsOptInKeywordCommands.YES,
    SmsOptInKeywordCommands.SUBSCRIBE,
    SmsOptInKeywordCommands.JOIN,
    SmsOptInKeywordCommands.SIGNUP,
    SmsOptInKeywordCommands.OPTIN,
    SmsOptInKeywordCommands.GO,
    SmsOptInKeywordCommands.UNSTOP,
    SmsOptInKeywordCommands.RESUME,
    SmsOptInKeywordCommands.CONTINUE,
    SmsOptInKeywordCommands.CONFIRM,
    SmsOptInKeywordCommands.ACTIVATE,
    SmsOptInKeywordCommands.ENABLE,
    SmsOptInKeywordCommands.REGISTER,
    SmsOptInKeywordCommands.ADD,
    SmsOptInKeywordCommands.OK,
    SmsOptInKeywordCommands.ALLOW,
    SmsOptInKeywordCommands.AGREE,
  ],
  [SmsCommandIntent.OPT_OUT]: [
    SmsCommands.MUTE_QUOTES,
    SmsOptOutKeywordCommands.STOP,
    SmsOptOutKeywordCommands.UNSUBSCRIBE,
    SmsOptOutKeywordCommands.CANCEL,
    SmsOptOutKeywordCommands.END,
    SmsOptOutKeywordCommands.QUIT,
    SmsOptOutKeywordCommands.STOPALL,
    SmsOptOutKeywordCommands.OPTOUT,
    SmsOptOutKeywordCommands.REMOVE,
    SmsOptOutKeywordCommands.DELETE,
    SmsOptOutKeywordCommands.BLOCK,
    SmsOptOutKeywordCommands.NO,
    SmsOptOutKeywordCommands.DISABLE,
    SmsOptOutKeywordCommands.LEAVE,
    SmsOptOutKeywordCommands.PAUSE,
  ],
} as const;

function normalizeSmsCommandKeyword(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');
}

const normalizedOptInKeywords = new Set(
  smsCommandKeywordAliases[SmsCommandIntent.OPT_IN].map((keyword) =>
    normalizeSmsCommandKeyword(keyword)
  )
);

const normalizedOptOutKeywords = new Set(
  smsCommandKeywordAliases[SmsCommandIntent.OPT_OUT].map((keyword) =>
    normalizeSmsCommandKeyword(keyword)
  )
);

export function getSmsCommandIntentFromKeyword(
  value: string
): SmsCommandIntentType | null {
  const normalizedKeyword = normalizeSmsCommandKeyword(value);

  if (!normalizedKeyword) {
    return null;
  }

  if (normalizedOptInKeywords.has(normalizedKeyword)) {
    return SmsCommandIntent.OPT_IN;
  }

  if (normalizedOptOutKeywords.has(normalizedKeyword)) {
    return SmsCommandIntent.OPT_OUT;
  }

  return null;
}
